import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Attach JWT from localStorage (works even when cross-origin cookies are blocked)
api.interceptors.request.use((config) => {
  const token = typeof window !== "undefined" ? window.localStorage.getItem("tios_token") : null;
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function errMsg(e, fallback = "Something went wrong.") {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x))).join(" ");
  }
  if (d && typeof d.msg === "string") return d.msg;
  return String(d);
}

export const Auth = {
  me: () => api.get("/auth/me").then((r) => r.data),
  login: (email, password) =>
    api.post("/auth/login", { email, password }).then((r) => {
      const { access_token, user } = r.data || {};
      if (access_token) window.localStorage.setItem("tios_token", access_token);
      return user;
    }),
  logout: () =>
    api.post("/auth/logout").finally(() => {
      window.localStorage.removeItem("tios_token");
    }).then((r) => r?.data),
  changePassword: (payload) => api.post("/auth/change-password", payload).then((r) => r.data),
};

export const Users = {
  list: (includeInactive) => api.get(`/users${includeInactive ? "?include_inactive=true" : ""}`).then((r) => r.data),
  create: (payload) => api.post("/users", payload).then((r) => r.data),
  update: (id, payload) => api.patch(`/users/${id}`, payload).then((r) => r.data),
  resetPassword: (id) => api.post(`/users/${id}/reset-password`).then((r) => r.data),
  remove: (id) => api.delete(`/users/${id}`).then((r) => r.data),
  restore: (id) => api.post(`/users/${id}/restore`).then((r) => r.data),
};

export const Requests = {
  list: () => api.get("/requests").then((r) => r.data),
  create: (payload) => api.post("/requests", payload).then((r) => r.data),
  approve: (id, comment) => api.post(`/requests/${id}/approve`, { comment }).then((r) => r.data),
  deny: (id, comment) => api.post(`/requests/${id}/deny`, { comment }).then((r) => r.data),
  adminLog: (payload) => api.post("/admin/log-request", payload).then((r) => r.data),
  remove: (id) => api.delete(`/requests/${id}`).then((r) => r.data),
  update: (id, payload) => api.patch(`/requests/${id}`, payload).then((r) => r.data),
};

export const Attachments = {
  upload: (requestId, file, onProgress) => {
    const form = new FormData();
    form.append("file", file);
    return api
      .post(`/requests/${requestId}/attachments`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (onProgress && evt.total) onProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      })
      .then((r) => r.data);
  },
  list: (requestId) => api.get(`/requests/${requestId}/attachments`).then((r) => r.data),
  remove: (attId) => api.delete(`/attachments/${attId}`).then((r) => r.data),
  downloadUrl: (attId) => `${API}/attachments/${attId}`,
  openInNewTab: async (attId, filename) => {
    // Fetch with auth header, convert to blob URL, open in new tab
    const resp = await api.get(`/attachments/${attId}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url;
    if (filename) a.download = filename;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
  },
};

export default api;
